import { useLayoutEffect, useRef, useState } from 'react'
import { unionNodeGhIds } from '../../utils/computeGroupTree'
import { useStationLabelStore } from '../../store/useStationLabelStore'

function formatDateStr(dateStr) {
  const [y, m, d] = dateStr.split('-')
  return `${d}.${m}.${y}`
}

// Pole stanice zobrazitelná v posledním hlavičkovém řádku (sloupec = stanice) — uživatel si volí
// zaškrtávátky, kolik/která pole se má zobrazit, každé vybrané pole na svém řádku pod sebou.
const STATION_LABEL_FIELDS = [
  { key: 'name',      label: 'Název' },
  { key: 'full_name', label: 'Plný název' },
  { key: 'qual',      label: 'Upřesnění' },
  { key: 'gh_id',     label: 'GH ID' },
]

// Max. počet řádků hlavičky tabulky CELKEM (skupinové úrovně stromu + řádek se stanicemi). Pokud
// má strom víc skupinových úrovní, než se vejde, hlubší úrovně se sloučí do labelu posledního
// zobrazeného řádku, oddělené " / " (např. "Brno / Prostorový shluk 6"). Při MAX_HEADER_ROWS=1
// se sloučí úplně vše (i jméno stanice) do jediného řádku.
const MAX_HEADER_ROWS = 2

// Tabulka pro produkt "Měřené veličiny" — řádky = časy, sloupce = stanice, hlavička je vícevrstvá
// podle hloubky stromu skupin (1 hlavičkový řádek na úroveň mezi vybraným uzlem/kořenem a listy),
// viz doc/plans/station-group-meas.md sekce 4a. Princip colSpan seskupení stejný jako u
// YlyStatsGrid.jsx, jen v hlavičce místo v řádcích a s libovolnou hloubkou místo pevných 2 řádků.
//
// Pořadí sloupců MUSÍ vycházet ze stromu (napřed všechny stanice 1. větve pohromadě, pak 2. větve
// atd., ve stejném pořadí jako ve stromu vlevo — ne abecedně podle labelu skupiny/shluku, jinak by
// např. "Prostorový shluk 10" skončil hned za "Prostorový shluk 1", ne za "Prostorový shluk 9" jak
// je ve stromu) a NE z globálního abecedního řazení napříč celou tabulkou — jinak by colSpan v
// hlavičce neodpovídal tomu, které sloupce pod ním skutečně leží. Abecední řazení se použije jen
// pro stanice UVNITŘ jednoho listu.

// Vrátí sloupce listů v pořadí daném stromem (sourozenci v pořadí jako ve stromu, uvnitř listu
// stanice abecedně podle jména) — stejné pořadí musí použít i buildHeaderRows pro colSpan bloky.
// Sloupec = { colKey, gh, branch } — branch je pole uzlů (root → list) od kořenového "nodes" po
// samotný list, kterým sloupec vznikl. Díky branch umí buildHeaderRows přiřadit každý sloupec
// přesně ke "svým" hlavičkovým buňkám podle identity uzlu (referenční rovnost), ne podle gh_id —
// to je nutné, protože stejné gh_id se může objevit ve dvou různých (překrývajících se)
// podskupinách a každý výskyt musí zůstat pod svou vlastní větví hlavičky, i když by test
// "own.has(gh)" vyšel true pro obě větve zároveň.
// Dedup se dělá jen VNITŘ jednoho nejvyššího uzlu z "nodes" (aby se stejné gh_id neopakovalo
// vícekrát v rámci jedné a téže podskupiny, což by nastalo, kdyby se stejná stanice objevila ve
// dvou různých listech pod týmž uzlem) — napříč různými uzly v "nodes" (sourozenecké podskupiny)
// se dedup záměrně NEDĚLÁ, viz uživatelský požadavek: stanice patřící do 2 podskupin má mít
// v tabulce viditelnou časovou řadu v OBOU.
// Sestaví seznam listových uzlů (uzly bez dětí) v pořadí ze stromu, spolu s jejich cestou od kořene.
function collectLeavesWithBranch(node, branch) {
  const path = [...branch, node]
  if (!node.children || node.children.length === 0) return [{ node, path }]
  return node.children.flatMap(child => collectLeavesWithBranch(child, path))
}

function leafGhIdsOrdered(nodes, stationsByGhId) {
  const result = []
  for (const topNode of nodes) {
    const seen = new Set()
    const leaves = collectLeavesWithBranch(topNode, [])
    for (const { node: leaf, path } of leaves) {
      const leafGhIds = [...unionNodeGhIds(leaf)].sort((a, b) => {
        const na = stationsByGhId.get(a)?.name ?? a
        const nb = stationsByGhId.get(b)?.name ?? b
        return na.localeCompare(nb, 'cs')
      })
      for (const gh of leafGhIds) {
        if (seen.has(gh)) continue
        seen.add(gh)
        result.push({ colKey: `${topNode.label}::${leaf.label}::${gh}`, gh, branch: path })
      }
    }
  }
  return result
}

// Sestaví hlavičkové řádky rekurzivně: headerRows[0] = nejvyšší úroveň (nodes samotné — přímé
// děti vybraného uzlu, nebo root uzly na kořeni), atd. Každá buňka = { label, colSpan }. Vygeneruje
// se 1 řádek pro samotné "nodes" (i když jsou to už listy — viz doc/plans/station-group-meas.md
// sekce 4a, příklad "Brno (8)": pobočka je list, ale pořád je to 1 úroveň hierarchie nad
// jednotlivými stanicemi a musí se v hlavičce objevit), a pak se rekurzivně sestupuje jen tam, kde
// uzel má další děti — jakmile žádný uzel na dané úrovni nemá děti, sestup se zastaví. Uzly na
// každé úrovni se řadí stejně (v pořadí ze stromu, ne abecedně) jako v leafGhIdsOrdered, aby colSpan
// odpovídal skutečnému pořadí sloupců.
//
// Souběžně počítá i levelStarts[i] = Set indexů do columns, kde na úrovni i (headerRows[i]) začíná
// nová skupina — používá se k protažení stejné oddělovací čáry i skrz řádek se stanicemi a datové
// řádky v tbody, ne jen v samotném segmentačním řádku hlavičky (viz doc/plans/station-group-meas.md
// sekce 4a: čára mezi shluky uvnitř jedné pobočky, ne jen mezi pobočkami na nejvyšší úrovni).
//
// colSpan/starts se pro každou úroveň počítá testem col.branch.includes(node) nad columns — funguje
// konzistentně i když se stejné gh_id vyskytuje ve dvou uzlech na stejné úrovni (překrývající se
// buckety/clustery, viz computeGroupTree.js), protože každý výskyt je samostatný sloupec s vlastní
// "branch" ukazující na jeho skutečný původ ve stromu (viz leafGhIdsOrdered).
// maxGroupRows = kolik skupinových úrovní smí headerRows mít (MAX_HEADER_ROWS - 1, řádek stanic
// se počítá zvlášť) — pokud má strom víc úrovní, přebytečné hlubší úrovně (od maxGroupRows dál) se
// slijí do labelu poslední ponechané úrovně, spojené " / " (viz MAX_HEADER_ROWS výše). Při
// maxGroupRows <= 0 se slijí úplně všechny skupinové úrovně přímo do stationLabelPrefix, který
// volající (MeasDataGrid) přidá před label stanice v posledním řádku.
//
// columns = pole { colKey, gh, branch } z leafGhIdsOrdered — colSpan/prefix se počítá podle toho,
// zda daný uzel hlavičky leží na "branch" (cestě od kořene k listu) daného sloupce, testem přes
// referenční rovnost uzlu (col.branch.includes(node)), NE podle gh_id. To je důležité, když se
// stejné gh_id vyskytuje ve dvou různých sourozeneckých větvích (překrývající se podskupiny) —
// každý výskyt je samostatný sloupec s vlastní "branch", takže spadá pod hlavičkovou buňku jen té
// větve, ze které skutečně pochází, ne obou.
function buildHeaderRows(nodes, columns, maxGroupRows) {
  // Klik přímo na list (nodes = [1 list] — jediná stanice, žádná pobočka/kategorie nad ní): žádný
  // podnadpis netřeba, sloupec stanice sám nese svůj název v posledním řádku.
  if (nodes.length === 1 && (!nodes[0].children || nodes[0].children.length === 0)) {
    return { rows: [], levelStarts: [], stationLabelPrefix: new Map() }
  }

  // 1) Plná hierarchie beze změny — stejný postup jako dřív, jen si navíc pamatujeme pro každou
  // úroveň i samotný uzel (node), abychom mohli později testovat příslušnost sloupce přes branch.
  const fullLevelOwn = [] // fullLevelOwn[i] = pole { label, node } pro úroveň i
  let level = nodes.map(n => ({ node: n }))

  while (level.length > 0) {
    fullLevelOwn.push(level.map(({ node }) => ({ label: node.label, node })))
    level = level.flatMap(({ node }) => {
      if (!node.children || node.children.length === 0) return []
      return node.children.map(child => ({ node: child }))
    })
  }

  // 2) Komprimace: ponech POSLEDNÍCH maxGroupRows úrovní (nejbližší k listům — nesou nejvíc
  // informace o rozdělení sloupců, tedy nejvíc samostatných buněk/colSpan bloků), přebytečné
  // NADŘAZENÉ úrovně (blíž ke kořeni) se slijí jako prefix do labelu první ponechané úrovně
  // (např. maxGroupRows=1: "Brno" nad "Prostorový shluk 6"/"7" se slije na "Brno / Prostorový
  // shluk 6" a "Brno / Prostorový shluk 7" — pořád 2 samostatné buňky se 2 různými colSpan, ne 1
  // buňka "Brno" přes celou šířku). Pokud maxGroupRows <= 0, slij VŠECHNY úrovně do
  // stationLabelPrefix (žádný samostatný skupinový řádek nevznikne, prefix se použije až u labelu
  // stanice v posledním řádku).
  const keepLevels = Math.max(0, maxGroupRows)
  const overflowCount = Math.max(0, fullLevelOwn.length - keepLevels)
  const overflow = fullLevelOwn.slice(0, overflowCount)
  const kept = fullLevelOwn.slice(overflowCount)

  const stationLabelPrefix = new Map() // colKey → spojený label přebytečných úrovní (jen když keepLevels===0)

  if (overflow.length > 0) {
    // Pro každý sloupec najdi jeho cestu přes přebytečné (nadřazené) úrovně a spoj " / ".
    const pathPrefixByCol = new Map()
    columns.forEach((col, ci) => {
      const parts = []
      for (const levelEntries of overflow) {
        const entry = levelEntries.find(e => col.branch.includes(e.node))
        if (entry) parts.push(entry.label)
      }
      if (parts.length > 0) pathPrefixByCol.set(ci, parts.join(' / '))
    })

    if (keepLevels > 0) {
      // První ponechaná úroveň (nejblíž kořeni z těch, co zůstaly) dostane prefix z nadřazených
      // úrovní přidaný PŘED svůj vlastní label — každá buňka na této úrovni zvlášť (různé buňky
      // mohou mít různý prefix, pokud spadají pod různé nadřazené uzly).
      const firstKept = kept[0]
      let idx = 0
      for (const entry of firstKept) {
        const colSpan = columns.reduce((n, col) => n + (col.branch.includes(entry.node) ? 1 : 0), 0)
        const colIdxsUnderEntry = Array.from({ length: colSpan }, (_, k) => idx + k)
        const prefixes = new Set(colIdxsUnderEntry.map(ci => pathPrefixByCol.get(ci)).filter(Boolean))
        if (prefixes.size === 1) {
          entry.label = `${[...prefixes][0]} / ${entry.label}`
        }
        idx += colSpan
      }
    } else {
      for (const [ci, prefix] of pathPrefixByCol) stationLabelPrefix.set(columns[ci].colKey, prefix)
    }
  }

  // 3) Z kept vygeneruj rows/levelStarts stejným colSpan mechanismem jako dřív.
  const rows = []
  const levelStarts = []
  for (const levelEntries of kept) {
    const row = []
    const starts = new Set()
    let idx = 0
    for (const { label, node } of levelEntries) {
      starts.add(idx)
      const colSpan = columns.reduce((n, col) => n + (col.branch.includes(node) ? 1 : 0), 0)
      row.push({ label, colSpan })
      idx += colSpan
    }
    rows.push(row)
    levelStarts.push(starts)
  }

  return { rows, levelStarts, stationLabelPrefix }
}

export function MeasDataGrid({ nodes, stationsByGhId, dataMap, labelMap, dayMap, times, unit, orientation = 'normal', granKey }) {
  const columns = leafGhIdsOrdered(nodes, stationsByGhId)
  const { rows: headerRows, levelStarts, stationLabelPrefix } = buildHeaderRows(nodes, columns, MAX_HEADER_ROWS - 1)
  // Sjednocení hranic ze VŠECH úrovní hierarchie (ne jen nejvyšší) — na obrázku z plánu "Brno →
  // Prostorový shluk 2/3 → stanice" musí čára oddělovat shluky uvnitř Brna, i když Brno samo je
  // jediná top-level skupina (žádná vlastní hranice na úrovni 0).
  const groupStarts = new Set(levelStarts.flatMap(s => [...s]))
  const labelFields = useStationLabelStore(s => s.labelFields)
  const setLabelFields = useStationLabelStore(s => s.setLabelFields)

  function toggleField(key) {
    setLabelFields(labelFields.includes(key) ? labelFields.filter(k => k !== key) : [...labelFields, key])
  }

  const cb = 'border-b border-gray-200 px-2 py-0.5 text-xs text-right tabular-nums whitespace-nowrap'
  const thBase = 'border-r border-r-gray-200 px-2 py-1 text-xs font-normal text-center align-top text-gray-800 bg-gray-50 whitespace-nowrap'

  // Oddělit skupiny časů slabší čárou – stejně jako u 1 stanice:
  //  - 10min data (min-prehled): čára na každé celé hodině (popisek končí ":00"),
  //  - hodinová data (hly-prehled): čára po 6 hodinách (00/06/12/18),
  //  - denní data (dly-prehled): čára po pentádách před dny 6/11/16/21/26.
  const hourBorder = time => {
    if (granKey === '10m') return String(labelMap?.get(time) ?? time).endsWith(':00')
    if (granKey === 'hly') {
      const label = String(labelMap?.get(time) ?? time)
      return ['00:00', '06:00', '12:00', '18:00'].some(h => label.endsWith(h))
    }
    if (granKey === 'dly') {
      const day = parseInt(String(time).slice(8, 10), 10)
      return [6, 11, 16, 21, 26].includes(day)
    }
    return false
  }

  const labelPicker = (
    <div className="flex flex-wrap items-center gap-3 pb-2">
      <span className="text-xs text-gray-400 font-medium uppercase tracking-wide">Popisek stanice</span>
      {STATION_LABEL_FIELDS.map(f => (
        <label key={f.key} className="flex items-center gap-1 text-xs text-gray-600 cursor-pointer">
          <input
            type="checkbox"
            checked={labelFields.includes(f.key)}
            onChange={() => toggleField(f.key)}
            className="accent-blue-600"
          />
          {f.label}
        </label>
      ))}
    </div>
  )

  if (orientation === 'transposed') {
    return (
      <div className="h-full flex flex-col">
        {labelPicker}
        <MeasDataGridTransposed
          columns={columns}
          headerRows={headerRows}
          groupStarts={groupStarts}
          stationLabelPrefix={stationLabelPrefix}
          stationsByGhId={stationsByGhId}
          dataMap={dataMap}
          labelMap={labelMap}
          dayMap={dayMap}
          times={times}
          unit={unit}
          labelFields={labelFields}
          hourBorder={hourBorder}
        />
      </div>
    )
  }

  return (
    <div className="h-full flex flex-col">
      {labelPicker}
      <div className="flex-1 min-h-0 overflow-hidden border border-gray-200">
        <div className="h-full overflow-auto">
        <table className="border-separate border-spacing-0 border-r-2 border-r-gray-400 border-b-2 border-b-gray-400 text-xs">
          <thead className="sticky top-0 z-20">
            {/* "Čas" buňka je vždy vlastní první řádek hlavičky (rowSpan přes všechny hlavičkové
                řádky + řádek se stanicemi) — vykreslena nezávisle na headerRows.length, aby na
                listovém uzlu (headerRows=[], žádný segmentační řádek pobočky) nedošlo k posunu
                sloupců, kdy by první stanice omylem "seděla" pod chybějící buňkou Čas. */}
            <tr>
              <th
                rowSpan={Math.max(headerRows.length, 1) + 1}
                className="sticky left-0 z-30 bg-gray-50 border-t-2 border-t-gray-400 border-l-2 border-l-gray-400 border-b-2 border-b-gray-400 border-r-2 border-r-gray-400 px-3 py-1 text-left align-top text-gray-800 font-semibold whitespace-nowrap"
              >
                Čas {unit && <span className="text-gray-500 ml-1 text-[10px]">({unit})</span>}
              </th>
              {headerRows[0]?.map((cell, ci) => (
                <th
                  key={`0-${ci}`}
                  colSpan={cell.colSpan}
                  className={`${thBase} border-t-2 border-t-gray-400 border-b border-b-gray-300 ${ci > 0 ? 'border-l-2 border-l-gray-400' : ''} font-medium`}
                >
                  {cell.label}
                </th>
              ))}
            </tr>
            {headerRows.slice(1).map((row, ri) => (
              <tr key={`h${ri + 1}`}>
                {row.map((cell, ci) => (
                  <th
                    key={`${ri + 1}-${ci}`}
                    colSpan={cell.colSpan}
                    className={`${thBase} border-b border-b-gray-300 ${ci > 0 ? 'border-l-2 border-l-gray-400' : ''} font-medium`}
                  >
                    {cell.label}
                  </th>
                ))}
              </tr>
            ))}
            <tr>
              {columns.map((col, gi) => {
                const station = stationsByGhId.get(col.gh)
                const prefix = stationLabelPrefix.get(col.colKey)
                const rawLines = labelFields.length > 0 ? labelFields.map(key => station?.[key] ?? '') : [col.gh]
                // Prefix (sloučené skupinové úrovně, jen když maxGroupRows<=0 — viz buildHeaderRows)
                // se spojí " / " s prvním řádkem popisku, ne jako samostatný řádek navíc.
                const lines = prefix ? [`${prefix} / ${rawLines[0]}`, ...rawLines.slice(1)] : rawLines
                const isGroupStart = gi > 0 && groupStarts.has(gi)
                return (
                  <th
                    key={col.colKey}
                    className={`${thBase} border-r-0 border-b-2 border-b-gray-400 ${headerRows.length === 0 ? 'border-t-2 border-t-gray-400' : ''} ${
                      isGroupStart ? 'border-l-2 border-l-gray-400' : gi > 0 ? 'border-l border-l-gray-300' : ''
                    }`}
                  >
                    {lines.map((line, li) => <div key={li}>{line}</div>)}
                  </th>
                )
              })}
            </tr>
          </thead>
          <tbody>
            {times.map((time, idx) => {
              const entry = dataMap.get(time)
              const label = labelMap.get(time) ?? time
              const info = dayMap?.get(time)
              const prevTime = idx > 0 ? times[idx - 1] : null
              const dayBorder = !!prevTime && dayMap?.get(time)?.dateStr !== dayMap?.get(prevTime)?.dateStr
              const rowRule = dayBorder
                ? 'border-t-2 border-t-gray-400'
                : (idx > 0 && hourBorder(time)) ? 'border-t border-t-gray-400' : ''
              return (
                <tr key={time} className="group hover:bg-slate-50">
                  <td className={`sticky left-0 z-10 bg-gray-50 group-hover:bg-slate-100 border-b border-l-2 border-l-gray-400 border-r-2 border-r-gray-400 px-3 py-0.5 text-right text-gray-700 whitespace-nowrap font-mono ${rowRule}`}>
                    {dayBorder && info?.dateStr ? (
                      <span className="flex flex-col items-end gap-0">
                        <span className="text-gray-400 text-xs">{formatDateStr(info.dateStr)}</span>
                        <span>{label}</span>
                      </span>
                    ) : label}
                  </td>
                  {columns.map((col, gi) => {
                    const value = entry?.[col.gh] ?? null
                    const isGroupStart = gi > 0 && groupStarts.has(gi)
                    return (
                      <td
                        key={col.colKey}
                        className={`${cb} ${
                          isGroupStart ? 'border-l-2 border-l-gray-400' : gi > 0 ? 'border-l border-l-gray-200' : ''
                        } ${rowRule} bg-white group-hover:bg-slate-50 ${value == null ? 'text-gray-300' : 'text-gray-700'}`}
                      >
                        {value != null ? value : '—'}
                      </td>
                    )
                  })}
                </tr>
              )
            })}
            {times.length === 0 && (
              <tr>
                <td colSpan={columns.length + 1} className="px-4 py-6 text-center text-gray-400">
                  Žádná data
                </td>
              </tr>
            )}
          </tbody>
        </table>
        </div>
      </div>
    </div>
  )
}

// ── Transponovaná varianta ─────────────────────────────────────────────────────
// řádky = stanice, sloupce = časy. 2 sticky sloupce vlevo: skupina (pobočka) + stanice.
// left offset sloupce "stanice" se MĚŘÍ ze skutečné šířky sloupce "skupina" (ref) – pevná hodnota
// nefunguje, protože sloupec se roztahuje podle nejdelšího názvu pobočky (whitespace-nowrap).
function MeasDataGridTransposed({
  columns, headerRows, groupStarts, stationLabelPrefix, stationsByGhId,
  dataMap, labelMap, dayMap, times, unit, labelFields, hourBorder,
}) {
  // Oddělovač mezi skupinami (pobočkami) – jen barva, tenká čára. Ne na 1. řádku (tam už je
  // spodní hrana hlavičky).
  const topRule = ri => (ri > 0 && groupStarts.has(ri) ? 'border-t border-t-gray-400' : '')

  // Šířka 1. sticky sloupce (skupina) → left offset 2. sticky sloupce (stanice).
  const groupColRef = useRef(null)
  const [stationLeft, setStationLeft] = useState(0)
  useLayoutEffect(() => {
    const el = groupColRef.current
    if (!el) return
    const measure = () => setStationLeft(el.getBoundingClientRect().width)
    measure()
    const ro = new ResizeObserver(measure)
    ro.observe(el)
    return () => ro.disconnect()
  }, [columns, labelFields, headerRows])

  // Svislá čára na hranici dne (2px) / celé hodiny u 10min dat (1px) mezi sloupci časů.
  const colRule = (time, idx) => {
    const prevTime = idx > 0 ? times[idx - 1] : null
    const dayBorder = !!prevTime && dayMap?.get(time)?.dateStr !== dayMap?.get(prevTime)?.dateStr
    if (dayBorder) return 'border-l-2 border-l-gray-400'
    if (idx > 0 && hourBorder?.(time)) return 'border-l border-l-gray-400'
    return ''
  }

  const cb = 'border-b border-r border-gray-200 px-2 py-0.5 text-xs text-right tabular-nums whitespace-nowrap'
  const th = 'border-b border-b-gray-200 border-r border-r-gray-200 px-2 py-1 text-xs font-normal text-center bg-gray-50 whitespace-nowrap'

  // Stromová hierarchie stanic (pobočka / …) je vlastní sticky sloupec vlevo. `headerRows` je
  // 1 úroveň (viz buildHeaderRows, MAX_HEADER_ROWS-1) – bloky nad pořadím `columns`.
  const groupBlocks = []
  {
    let idx = 0
    for (const cell of (headerRows[0] ?? [])) {
      groupBlocks.push({ label: cell.label, start: idx, span: cell.colSpan })
      idx += cell.colSpan
    }
  }
  const hasGroupCol = groupBlocks.length > 0
  const blockFor = ri => groupBlocks.find(x => ri >= x.start && ri < x.start + x.span)

  return (
    <div className="flex-1 min-h-0 overflow-hidden border border-gray-200">
      <div className="h-full overflow-auto">
        <table className="border-separate border-spacing-0 border-r-2 border-r-gray-400 border-b-2 border-b-gray-400 text-xs">
          <thead className="sticky top-0 z-20">
            <tr>
              {hasGroupCol && (
                <th
                  className="sticky left-0 z-30 bg-gray-50 border-t-2 border-t-gray-400 border-l-2 border-l-gray-400 border-b border-b-gray-400 border-r border-r-gray-400 px-3 py-1 text-left align-bottom text-gray-500 font-medium whitespace-nowrap"
                >
                  Skupina
                </th>
              )}
              <th
                className={`sticky z-30 bg-gray-50 border-t-2 border-t-gray-400 border-b border-b-gray-400 border-r border-r-gray-400 px-3 py-1 text-left align-bottom text-gray-800 font-semibold whitespace-nowrap ${hasGroupCol ? 'border-l border-l-gray-400' : 'left-0 border-l-2 border-l-gray-400'}`}
                style={hasGroupCol ? { left: stationLeft } : undefined}
              >
                Stanice {unit && <span className="text-gray-500 ml-1 text-[10px]">({unit})</span>}
              </th>
              {times.map((time, idx) => {
                const label = labelMap.get(time) ?? time
                const info = dayMap?.get(time)
                const prevTime = idx > 0 ? times[idx - 1] : null
                const dayBorder = !!prevTime && dayMap?.get(time)?.dateStr !== dayMap?.get(prevTime)?.dateStr
                return (
                  <th key={time} className={`${th} border-t-2 border-t-gray-400 border-b-gray-400 text-gray-500 font-mono ${colRule(time, idx)}`}>
                    {dayBorder && info?.dateStr ? (
                      <span className="flex flex-col items-center gap-0">
                        <span className="text-gray-400 text-[10px] font-sans">{formatDateStr(info.dateStr)}</span>
                        <span>{label}</span>
                      </span>
                    ) : label}
                  </th>
                )
              })}
            </tr>
          </thead>
          <tbody>
            {columns.map((col, ri) => {
              const station = stationsByGhId.get(col.gh)
              const prefix = stationLabelPrefix.get(col.colKey)
              const rawLines = labelFields.length > 0 ? labelFields.map(key => station?.[key] ?? '') : [col.gh]
              const lines = prefix ? [`${prefix} / ${rawLines[0]}`, ...rawLines.slice(1)] : rawLines
              const entryByTime = time => dataMap.get(time)?.[col.gh] ?? null
              const block = blockFor(ri)
              const isBlockStart = !!block && block.start === ri
              return (
                <tr key={col.colKey} className="group hover:bg-slate-50">
                  {hasGroupCol && isBlockStart && (
                    <td
                      ref={ri === 0 ? groupColRef : undefined}
                      rowSpan={block.span}
                      title={block.label}
                      className={`sticky left-0 z-10 bg-gray-100 border-b border-l-2 border-l-gray-400 border-r border-r-gray-400 px-3 py-1 text-left align-top text-[11px] font-medium text-gray-600 whitespace-nowrap ${ri > 0 ? 'border-t border-t-gray-400' : ''}`}
                    >
                      {block.label}
                    </td>
                  )}
                  <td
                    className={`sticky z-10 bg-white group-hover:bg-slate-50 border-b border-r border-r-gray-400 px-3 py-0.5 text-left text-gray-700 whitespace-nowrap ${hasGroupCol ? 'border-l border-l-gray-400' : 'left-0 border-l-2 border-l-gray-400'} ${topRule(ri)}`}
                    style={hasGroupCol ? { left: stationLeft } : undefined}
                  >
                    {lines.map((line, li) => (
                      <div key={li} className={li > 0 ? 'text-[10px] text-gray-400' : ''}>{line}</div>
                    ))}
                  </td>
                  {times.map((time, ti) => {
                    const value = entryByTime(time)
                    return (
                      <td
                        key={time}
                        className={`${cb} ${colRule(time, ti)} ${topRule(ri)} bg-white group-hover:bg-slate-50 ${value == null ? 'text-gray-300' : 'text-gray-700'}`}
                      >
                        {value != null ? value : '—'}
                      </td>
                    )
                  })}
                </tr>
              )
            })}
            {columns.length === 0 && (
              <tr>
                <td colSpan={(hasGroupCol ? 2 : 1) + times.length} className="px-4 py-6 text-center text-gray-400">
                  Žádné stanice
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  )
}
